import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { DatabaseZap, Check, Clock, Loader2, AlertTriangle, ShieldCheck } from "lucide-react";
import { RoleGate } from "@/components/RoleGate";
import { deriveState, STATE_MESSAGES, SKELETON_ROWS, type ViewState } from "@/lib/states";

const STATUS_MAP: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  pending: { label: "Pendente", variant: "outline" },
  processing: { label: "Processando…", variant: "secondary" },
  completed: { label: "Concluído", variant: "default" },
  rejected: { label: "Rejeitado", variant: "destructive" },
};

export default function DataManagement() {
  const qc = useQueryClient();
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const requestsQuery = useQuery({
    queryKey: ["data-deletion-requests"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("data_deletion_requests")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const consentsQuery = useQuery({
    queryKey: ["data-consent-logs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("data_consent_logs")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return data;
    },
  });

  const deleteMut = useMutation({
    mutationFn: async (requestId: string) => {
      const { data, error } = await supabase.functions.invoke("data-delete", {
        body: { request_id: requestId },
      });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: (data) => {
      const d = data?.deleted || {};
      toast.success(
        `Dados excluídos: ${d.messages || 0} mensagens, ${d.members || 0} membros, ${d.consents || 0} consentimentos`
      );
      qc.invalidateQueries({ queryKey: ["data-deletion-requests"] });
      qc.invalidateQueries({ queryKey: ["data-consent-logs"] });
    },
    onError: (e: Error) => toast.error(`Erro: ${e.message}`),
  });

  const reqState: ViewState = deriveState({
    data: requestsQuery.data,
    isLoading: requestsQuery.isLoading,
    isError: requestsQuery.isError,
  });

  const conState: ViewState = deriveState({
    data: consentsQuery.data,
    isLoading: consentsQuery.isLoading,
    isError: consentsQuery.isError,
  });

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <DatabaseZap className="h-6 w-6 text-primary" />
        <h1 className="text-2xl font-bold font-headline">Gestão de Dados (LGPD)</h1>
      </div>

      <Tabs defaultValue="requests">
        <TabsList>
          <TabsTrigger value="requests">Solicitações de Exclusão</TabsTrigger>
          <TabsTrigger value="consents">Consentimentos</TabsTrigger>
        </TabsList>

        <TabsContent value="requests">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Solicitações de Exclusão de Dados</CardTitle>
            </CardHeader>
            <CardContent>
              {reqState === "loading" && (
                <div className="space-y-3">
                  {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              )}

              {reqState === "error" && (
                <div className="text-center py-8 space-y-3">
                  <AlertTriangle className="h-8 w-8 text-destructive mx-auto" />
                  <p className="text-sm font-medium">{STATE_MESSAGES.error.title}</p>
                  <p className="text-xs text-muted-foreground">{STATE_MESSAGES.error.description}</p>
                  <Button variant="outline" size="sm" onClick={() => requestsQuery.refetch()}>
                    {STATE_MESSAGES.error.retryLabel}
                  </Button>
                </div>
              )}

              {reqState === "empty" && (
                <div className="text-center py-8 space-y-2">
                  <ShieldCheck className="h-8 w-8 text-muted-foreground/30 mx-auto" />
                  <p className="text-sm text-muted-foreground">Nenhuma solicitação registrada.</p>
                </div>
              )}

              {reqState === "success" && requestsQuery.data && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Solicitante</TableHead>
                      <TableHead>JID</TableHead>
                      <TableHead>Motivo</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {requestsQuery.data.map((r) => {
                      const st = STATUS_MAP[r.status] || STATUS_MAP.pending;
                      const isProcessing = deleteMut.isPending && deleteMut.variables === r.id;
                      return (
                        <TableRow key={r.id}>
                          <TableCell className="font-medium">{r.requester_name}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{r.requester_jid}</TableCell>
                          <TableCell className="text-sm max-w-xs truncate">{r.reason}</TableCell>
                          <TableCell>
                            <Badge variant={isProcessing ? "secondary" : st.variant}>
                              {isProcessing ? (
                                <span className="flex items-center gap-1">
                                  <Loader2 className="h-3 w-3 animate-spin" /> Excluindo…
                                </span>
                              ) : (
                                st.label
                              )}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {new Date(r.created_at).toLocaleDateString("pt-BR")}
                          </TableCell>
                          <TableCell>
                            {r.status === "pending" && (
                              <RoleGate permission="admin.panel" fallback={<span className="text-xs text-muted-foreground">Sem permissão</span>}>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={deleteMut.isPending}
                                  onClick={() => setConfirmId(r.id)}
                                >
                                  <Check className="h-3 w-3 mr-1" /> Executar
                                </Button>
                              </RoleGate>
                            )}
                            {r.status === "completed" && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Check className="h-3 w-3 text-primary" />
                                {r.completed_at && new Date(r.completed_at).toLocaleDateString("pt-BR")}
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="consents">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Registros de Consentimento</CardTitle>
            </CardHeader>
            <CardContent>
              {conState === "loading" && (
                <div className="space-y-3">
                  {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              )}

              {conState === "error" && (
                <div className="text-center py-8 space-y-3">
                  <AlertTriangle className="h-8 w-8 text-destructive mx-auto" />
                  <p className="text-sm font-medium">{STATE_MESSAGES.error.title}</p>
                  <Button variant="outline" size="sm" onClick={() => consentsQuery.refetch()}>
                    {STATE_MESSAGES.error.retryLabel}
                  </Button>
                </div>
              )}

              {conState === "empty" && (
                <p className="text-sm text-muted-foreground text-center py-8">Nenhum consentimento registrado.</p>
              )}

              {conState === "success" && consentsQuery.data && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Membro (JID)</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Concedido</TableHead>
                      <TableHead>IP</TableHead>
                      <TableHead>Data</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {consentsQuery.data.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell className="text-xs">{c.member_jid}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{c.consent_type}</Badge>
                        </TableCell>
                        <TableCell>
                          {c.granted ? (
                            <Check className="h-4 w-4 text-primary" />
                          ) : (
                            <Clock className="h-4 w-4 text-muted-foreground" />
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{c.ip_address || "-"}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {new Date(c.created_at).toLocaleDateString("pt-BR")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Confirmation dialog */}
      <AlertDialog open={!!confirmId} onOpenChange={() => setConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão de dados</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação excluirá permanentemente todas as mensagens, registros de membro e
              consentimentos associados ao solicitante. Esta operação é irreversível.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (confirmId) deleteMut.mutate(confirmId);
                setConfirmId(null);
              }}
            >
              Excluir dados permanentemente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
